import React, { useEffect, useCallback } from 'react';
import { Route as ReactRouteDom, type RouteProps, type RouteComponentProps } from 'react-router-dom';
import { prop } from 'ramda';
import { useAppSelector } from '@core/js/store/hooks';
import { languageService } from '@theme/js/services/language.service';
import * as urlUtils from '@theme/js/utils/url.util';

const WrappedRoute: React.FC<RouteComponentProps & Pick<RouteProps, 'component' | 'render'> & { useTrailingSlash: boolean }> = (props) => {
    const { location, history, useTrailingSlash, component: Component, render, children } = props;
    const { pathname } = location;

    /* Lugano changes - MWL-16252 */
    /* Attempt to retrieve the two-letter language code. Due to the asynchronous nature of Redux and React,
     the language may not be available during the initial page load.
     Therefore, we also check against all supported languages if no specific language is found.
    */

    const languages = languageService.getAllLocalesTwoLetterLanguage(); /* ['en', 'de', 'fr', 'it'] */

    const isLangPath = useCallback((suffix) => {
        return languages.some((lang: any) => pathname.endsWith(`/${lang}${suffix}`));
    }, [languages, pathname]);
    /* Lugano changes */

    /* Lugano changes */
    useEffect(() => {
        if (pathname === '/') {
            return;
        }

        const isLangPathExact = isLangPath(''); /* Lugano changes */
        const isLangPathWithSlash = isLangPath('/'); /* Lugano changes */

        if (isLangPathExact) {
            history.replace({
                ...location,
                pathname: `${pathname}/`
            });

            return;
        }

        const shouldAddTrailingSlash = useTrailingSlash && pathname.slice(-1) !== '/';
        const shouldRemoveTrailingSlash = !useTrailingSlash && pathname.slice(-1) === '/' && !isLangPathWithSlash;

        if (shouldAddTrailingSlash) {
            history.replace({
                ...location,
                pathname: `${pathname}/`
            });
        } else if (shouldRemoveTrailingSlash) {
            const langFromSearchParams = urlUtils.getURLParam(location.search, 'language'); /* Lugano changes */
            if (!langFromSearchParams) {
                history.replace({
                    ...location,
                    pathname: pathname.slice(0, -1)
                });
            }
        }
    }, [useTrailingSlash, pathname, location, history, isLangPath]);
    /* Lugano changes */

    if (Component) {
        return <Component {...props} />;
    }

    if (render) {
        return (
            <>
                {render(props)}
            </>
        );
    }

    return (
        <>
            {children}
        </>
    );
};

export const Route: React.FC<RouteProps> = (props) => {
    const { children, component, render, ...routeProps } = props;
    const { useTrailingSlash } = useAppSelector(prop('seo'));

    return (
        <ReactRouteDom {...routeProps} render={(compProps: RouteComponentProps) => (
            <WrappedRoute {...{ ...compProps, useTrailingSlash, component, render }} >
                {children}
            </WrappedRoute>
        )} />
    );
};
